import { ethers } from 'ethers';
import {
  UNISWAP_V2_ROUTER_ABI,
  UNISWAP_V2_FACTORY_ABI,
  UNISWAP_V2_PAIR_ABI,
  QUOTER_V2_STATIC_ABI,
  UNISWAP_V3_FACTORY_ABI,
  UNISWAP_V3_POOL_ABI,
} from '../contracts/abisV2';
import { 
  DEX_CONFIGS, 
  V3_FEE_TIERS_ARRAY,
  FEE_CONFIG,
  TOKENS,
} from '../config/constantsV2';

// Fast routing for whitelisted tokens - only WFTM since all whitelisted tokens are paired with FTM
const WHITELISTED_INTERMEDIATE_TOKENS = [
  TOKENS.WFTM.address,      // WFTM - all whitelisted tokens have FTM pairs
];

// Complete routing for custom tokens - check all possible intermediates
const CUSTOM_INTERMEDIATE_TOKENS = [
  TOKENS.WFTM.address,      // WFTM - most common and highest liquidity
  TOKENS.AXLUSDC.address,   // axlUSDC - good for stablecoin swaps
  TOKENS.LIF3_OLD.address,  // LIF3 OLD - paired with LSHARE OLD in original protocol
  TOKENS.L3USD.address,     // L3USD - native stablecoin
  TOKENS.LIF3_NEW.address,  // LIF3 - protocol token
  TOKENS.TOMB.address,      // TOMB - additional liquidity option
];

// Determine which intermediate tokens to use based on whether both tokens are whitelisted
const getIntermediateTokens = (tokenIn, tokenOut) => {
  const bothWhitelisted = tokenIn.isWhitelisted && tokenOut.isWhitelisted;
  return bothWhitelisted ? WHITELISTED_INTERMEDIATE_TOKENS : CUSTOM_INTERMEDIATE_TOKENS;
};

// Helper to get WFTM address based on network
const getWFTMAddress = () => TOKENS.WFTM.address;

// Helper to convert native token address to WFTM for routing
const normalizeTokenAddress = (tokenAddress) => {
  if (!tokenAddress || tokenAddress === ethers.ZeroAddress) {
    return getWFTMAddress();
  }
  return tokenAddress;
};

// Calculate fee amount (1%)
const calculateFee = (amount) => {
  const amountBN = ethers.toBigInt(amount);
  return (amountBN * ethers.toBigInt(FEE_CONFIG.FEE_PERCENTAGE)) / ethers.toBigInt(FEE_CONFIG.BASIS_POINTS);
};

// Get quote from a V2 DEX
export const getV2Quote = async (provider, dexConfig, tokenIn, tokenOut, amountIn) => {
  try {
    const router = new ethers.Contract(dexConfig.router, UNISWAP_V2_ROUTER_ABI, provider);
    const factory = new ethers.Contract(dexConfig.factory, UNISWAP_V2_FACTORY_ABI, provider);
    
    // Normalize addresses for routing
    const tokenInAddress = normalizeTokenAddress(tokenIn.address);
    const tokenOutAddress = normalizeTokenAddress(tokenOut.address);
    
    const allQuotes = [];
    
    console.log(`  🔄 Checking ${dexConfig.name} V2...`);
    console.log(`    Factory: ${dexConfig.factory}`);
    console.log(`    Router: ${dexConfig.router}`);
    console.log(`    Input: ${tokenIn.symbol} (${tokenInAddress})`);
    console.log(`    Output: ${tokenOut.symbol} (${tokenOutAddress})`);
    
    // Try direct path first - check factory for pool existence
    try {
      const pairAddress = await factory.getPair(tokenInAddress, tokenOutAddress);
      if (pairAddress !== ethers.ZeroAddress) {
        console.log(`    ℹ️  Direct pool exists at ${pairAddress.substring(0, 10)}...`);
        const path = [tokenInAddress, tokenOutAddress];
        const amounts = await router.getAmountsOut(amountIn, path);
        const amountOut = amounts[amounts.length - 1];
        
        if (amountOut > 0) {
          allQuotes.push({
            dex: dexConfig,
            path: path,
            amountOut: amountOut,
            type: 'v2',
            directPath: true,
          });
          console.log(`    ✓ Direct path found: ${ethers.formatUnits(amountOut, tokenOut.decimals)}`);
        }
      } else {
        console.log(`    ✗ Direct pool does not exist`);
      }
    } catch (error) {
      console.log(`    ✗ Direct path check failed: ${error.message}`);
    }
  
  // Always try multi-hop paths through intermediate tokens
  const intermediateTokens = getIntermediateTokens(tokenIn, tokenOut);
  for (const intermediateToken of intermediateTokens) {
    // Skip if one of the tokens is already the intermediate
    if (tokenInAddress === intermediateToken || tokenOutAddress === intermediateToken) {
      continue;
    }
    
    try {
      const intermediateSymbol = getTokenSymbol(intermediateToken);
      
        console.log(`    🔍 Checking multi-hop via ${intermediateSymbol} (${intermediateToken})...`);
        
        // Check if both pools exist in the factory
        const pair1 = await factory.getPair(tokenInAddress, intermediateToken);
        const pair2 = await factory.getPair(intermediateToken, tokenOutAddress);
        
        if (pair1 === ethers.ZeroAddress) {
          console.log(`    ✗ Multi-hop via ${intermediateSymbol}: First pool (${tokenIn.symbol}→${intermediateSymbol}) does not exist`);
          continue;
        }
        
        if (pair2 === ethers.ZeroAddress) {
          console.log(`    ✗ Multi-hop via ${intermediateSymbol}: Second pool (${intermediateSymbol}→${tokenOut.symbol}) does not exist`);
          continue;
        }
        
        console.log(`    ℹ️  Multi-hop via ${intermediateSymbol}: Both pools exist!`);
        console.log(`      Pool 1 (${tokenIn.symbol}→${intermediateSymbol}): ${pair1}`);
        console.log(`      Pool 2 (${intermediateSymbol}→${tokenOut.symbol}): ${pair2}`);
        
        // Now get the actual quote through the router
        const path = [tokenInAddress, intermediateToken, tokenOutAddress];
        const amounts = await router.getAmountsOut(amountIn, path);
        const amountOut = amounts[amounts.length - 1];
      
      if (amountOut > 0) {
        allQuotes.push({
          dex: dexConfig,
          path: path,
          amountOut: amountOut,
          type: 'v2',
          directPath: false,
          intermediateToken: intermediateToken,
        });
        console.log(`    ✓ Multi-hop via ${intermediateSymbol}: ${ethers.formatUnits(amountOut, tokenOut.decimals)} ${tokenOut.symbol}`);
      }
    } catch (error) {
      // Log the specific error for debugging
      const intermediateSymbol = getTokenSymbol(intermediateToken);
      console.log(`    ✗ Multi-hop via ${intermediateSymbol} failed: ${error.message || 'Unknown error'}`);
      continue;
    }
  }
  
  if (allQuotes.length === 0) {
    console.log(`    ✗ No routes found on ${dexConfig.name}`);
    return null;
  }
  
  // Return the best quote (highest output)
  return allQuotes.reduce((best, current) => 
    current.amountOut > best.amountOut ? current : best
  );
  } catch (error) {
    console.error(`    ❌ Error checking ${dexConfig.name}:`, error.message);
    return null;
  }
};

// Helper to get token symbol from address
const getTokenSymbol = (address) => {
  const addrLower = address.toLowerCase();
  if (addrLower === TOKENS.WFTM.address.toLowerCase()) return 'WFTM';
  if (addrLower === TOKENS.AXLUSDC.address.toLowerCase()) return 'axlUSDC';
  if (addrLower === TOKENS.L3USD.address.toLowerCase()) return 'L3USD';
  if (addrLower === TOKENS.LIF3_NEW.address.toLowerCase()) return 'LIF3';
  if (addrLower === TOKENS.TOMB.address.toLowerCase()) return 'TOMB';
  if (addrLower === TOKENS.LSHARE_OLD.address.toLowerCase()) return 'LSHARE_OLD';
  if (addrLower === TOKENS.LIF3_OLD.address.toLowerCase()) return 'LIF3_OLD';
  return address.substring(0, 8) + '...';
};

// Get quote from a V3 DEX for a specific fee tier
export const getV3QuoteForFee = async (provider, dexConfig, tokenIn, tokenOut, amountIn, fee) => {
  try {
    // Normalize addresses
    const tokenInAddress = normalizeTokenAddress(tokenIn.address);
    const tokenOutAddress = normalizeTokenAddress(tokenOut.address);
    
    // Check if pool exists
    const factory = new ethers.Contract(dexConfig.factory, UNISWAP_V3_FACTORY_ABI, provider);
    const poolAddress = await factory.getPool(tokenInAddress, tokenOutAddress, fee);
    
    if (poolAddress === ethers.ZeroAddress) {
      return null;
    }
    
    // Get quote using Quoter
    const quoter = new ethers.Contract(dexConfig.quoter, QUOTER_V2_STATIC_ABI, provider);
    
    // Use staticCall to simulate the call
    const amountOut = await quoter.quoteExactInputSingle.staticCall(
      tokenInAddress,
      tokenOutAddress,
      fee,
      amountIn,
      0 // sqrtPriceLimitX96 = 0 means no limit
    );
    
    return {
      dex: dexConfig,
      tokenIn: tokenInAddress,
      tokenOut: tokenOutAddress,
      fee: fee,
      amountOut: amountOut,
      type: 'v3',
      directPath: true,
      poolAddress: poolAddress,
    };
  } catch (error) {
    console.error(`Error getting V3 quote from ${dexConfig.name} with fee ${fee}:`, error);
    return null;
  }
};

// Get all V3 quotes for different fee tiers
export const getV3Quote = async (provider, dexConfig, tokenIn, tokenOut, amountIn) => {
  try {
    const allQuotes = [];
    
    // Try all fee tiers in parallel for direct paths
    const quotePromises = V3_FEE_TIERS_ARRAY.map(fee => 
      getV3QuoteForFee(provider, dexConfig, tokenIn, tokenOut, amountIn, fee)
    );
    
    const directQuotes = await Promise.all(quotePromises);
    
    // Add valid direct quotes
    const validDirectQuotes = directQuotes.filter(q => q !== null && q.amountOut > 0);
    allQuotes.push(...validDirectQuotes);
    
    // Always try multi-hop paths through intermediate tokens
    const multiHopQuote = await getV3QuoteWithIntermediates(provider, dexConfig, tokenIn, tokenOut, amountIn);
    if (multiHopQuote && multiHopQuote.amountOut > 0) {
      allQuotes.push(multiHopQuote);
    }
    
    if (allQuotes.length === 0) {
      return null;
    }
    
    // Return the quote with the highest output
    return allQuotes.reduce((best, current) => 
      current.amountOut > best.amountOut ? current : best
    );
  } catch (error) {
    console.error(`Error getting V3 quotes from ${dexConfig.name}:`, error);
    return null;
  }
};

// Get V3 quote with multiple intermediate token options
export const getV3QuoteWithIntermediates = async (provider, dexConfig, tokenIn, tokenOut, amountIn) => {
  try {
    const tokenInAddress = normalizeTokenAddress(tokenIn.address);
    const tokenOutAddress = normalizeTokenAddress(tokenOut.address);
    
    const bestQuotes = [];
    
    // Try each intermediate token based on whitelisting
    const intermediateTokens = getIntermediateTokens(tokenIn, tokenOut);
    for (const intermediateToken of intermediateTokens) {
      // Skip if one of the tokens is already the intermediate
      if (tokenInAddress === intermediateToken || tokenOutAddress === intermediateToken) {
        continue;
      }
      
      // Try all combinations of fee tiers for the two hops
      for (const fee1 of V3_FEE_TIERS_ARRAY) {
        for (const fee2 of V3_FEE_TIERS_ARRAY) {
          try {
            // Check if both pools exist
            const factory = new ethers.Contract(dexConfig.factory, UNISWAP_V3_FACTORY_ABI, provider);
            const pool1 = await factory.getPool(tokenInAddress, intermediateToken, fee1);
            const pool2 = await factory.getPool(intermediateToken, tokenOutAddress, fee2);
            
            if (pool1 === ethers.ZeroAddress || pool2 === ethers.ZeroAddress) {
              continue;
            }
            
            // Get quote for first hop
            const quoter = new ethers.Contract(dexConfig.quoter, QUOTER_V2_STATIC_ABI, provider);
            const amountOutHop1 = await quoter.quoteExactInputSingle.staticCall(
              tokenInAddress,
              intermediateToken,
              fee1,
              amountIn,
              0
            );
            
            // Get quote for second hop
            const amountOut = await quoter.quoteExactInputSingle.staticCall(
              intermediateToken,
              tokenOutAddress,
              fee2,
              amountOutHop1,
              0
            );
            
            bestQuotes.push({
              dex: dexConfig,
              path: [tokenInAddress, intermediateToken, tokenOutAddress],
              fees: [fee1, fee2],
              amountOut: amountOut,
              type: 'v3',
              directPath: false,
              intermediateToken: intermediateToken,
            });
          } catch (error) {
            // Skip this combination
            continue;
          }
        }
      }
    }
    
    if (bestQuotes.length === 0) {
      return null;
    }
    
    // Return the best quote
    return bestQuotes.reduce((best, current) => 
      current.amountOut > best.amountOut ? current : best
    );
  } catch (error) {
    console.error(`Error getting V3 quote with intermediates from ${dexConfig.name}:`, error);
    return null;
  }
};

// Get quotes from all DEXes
export const getAllQuotes = async (provider, tokenIn, tokenOut, amountIn) => {
  try {
    const amountInWei = ethers.parseUnits(
      amountIn.toString(),
      tokenIn.decimals
    );
    
    console.log(`🔍 Fetching quotes for ${amountIn} ${tokenIn.symbol} → ${tokenOut.symbol}`);
    
    // Get all enabled DEXes
    const enabledDexes = Object.values(DEX_CONFIGS).filter(dex => dex.enabled);
    
    // Get quotes from all DEXes in parallel (single-DEX routing)
    const quotePromises = enabledDexes.map(async (dex) => {
      if (dex.type === 'v2') {
        return getV2Quote(provider, dex, tokenIn, tokenOut, amountInWei);
      } else if (dex.type === 'v3') {
        return getV3Quote(provider, dex, tokenIn, tokenOut, amountInWei);
      }
      return null;
    });
    
    const quotes = await Promise.all(quotePromises);
    
    // Filter out null results
    const validQuotes = quotes.filter(q => q !== null && q.amountOut > 0);
    
    // Try cross-DEX routing (2-hop across different DEXes)
    console.log(`🔀 Checking cross-DEX routes...`);
    const crossDexQuotes = await getCrossDexQuotes(provider, enabledDexes, tokenIn, tokenOut, amountInWei);
    validQuotes.push(...crossDexQuotes);
    
    // Log all quotes for debugging
    console.log(`📊 Found ${validQuotes.length} valid quotes:`);
    validQuotes.forEach(q => {
      const amountOutFormatted = ethers.formatUnits(q.amountOut, tokenOut.decimals);
      let pathInfo = '';
      if (q.crossDex) {
        pathInfo = `Cross-DEX: ${q.dex1.name} → ${q.dex2.name}`;
      } else if (q.directPath) {
        pathInfo = 'Direct path';
      } else if (q.path) {
        // Show the actual token symbols in the path
        const pathSymbols = q.path.map(addr => {
          if (addr.toLowerCase() === TOKENS.WFTM.address.toLowerCase()) return 'WFTM';
          if (addr.toLowerCase() === TOKENS.AXLUSDC.address.toLowerCase()) return 'axlUSDC';
          if (addr.toLowerCase() === TOKENS.L3USD.address.toLowerCase()) return 'L3USD';
          if (addr.toLowerCase() === TOKENS.LIF3_NEW.address.toLowerCase()) return 'LIF3';
          if (addr.toLowerCase() === TOKENS.TOMB.address.toLowerCase()) return 'TOMB';
          if (addr.toLowerCase() === TOKENS.LSHARE_OLD.address.toLowerCase()) return 'LSHARE_OLD';
          if (addr.toLowerCase() === TOKENS.LIF3_OLD.address.toLowerCase()) return 'LIF3_OLD';
          return addr.substring(0, 6) + '...';
        });
        pathInfo = `Path: ${pathSymbols.join(' → ')}`;
      }
      console.log(`  - ${q.dex.name} (${q.type}): ${amountOutFormatted} ${tokenOut.symbol} [${pathInfo}]`);
    });
    
    if (validQuotes.length === 0) {
      console.error('❌ No valid routes found');
      return null;
    }
    
    // Find the best quote (highest output)
    const bestQuote = validQuotes.reduce((best, current) => 
      current.amountOut > best.amountOut ? current : best
    );
    
    console.log(`✅ Best route: ${bestQuote.dex.name} (${bestQuote.type}) - ${ethers.formatUnits(bestQuote.amountOut, tokenOut.decimals)} ${tokenOut.symbol}`);
    
    // Calculate 1% fee and deduct from display
    const feeAmount = calculateFee(bestQuote.amountOut);
    const amountOutAfterFee = bestQuote.amountOut - feeAmount;
    
    return {
      ...bestQuote,
      amountOutBeforeFee: bestQuote.amountOut,
      feeAmount: feeAmount,
      amountOut: amountOutAfterFee, // Show user 99% of output
      allQuotes: validQuotes.sort((a, b) => 
        b.amountOut > a.amountOut ? 1 : -1
      ),
    };
  } catch (error) {
    console.error('Error getting quotes from all DEXes:', error);
    throw error;
  }
};

// Get cross-DEX quotes (2-hop across different DEXes)
const getCrossDexQuotes = async (provider, dexes, tokenIn, tokenOut, amountIn) => {
  const crossDexQuotes = [];
  
  // Normalize addresses
  const tokenInAddress = normalizeTokenAddress(tokenIn.address);
  const tokenOutAddress = normalizeTokenAddress(tokenOut.address);
  
  // Try routing through WFTM across different DEXes
  const wftmAddress = TOKENS.WFTM.address;
  const axlUSDCAddress = TOKENS.AXLUSDC.address;
  
  // Determine bridge tokens based on whether tokens are whitelisted
  const bothWhitelisted = tokenIn.isWhitelisted && tokenOut.isWhitelisted;
  const bridgeTokens = bothWhitelisted ? [wftmAddress] : [wftmAddress];
  
  // For whitelisted tokens, only check WFTM bridge (all whitelisted are paired with FTM)
  // For cross-DEX between WFTM and axlUSDC, always check
  const shouldCheckAxlUSDC = tokenInAddress === wftmAddress || tokenOutAddress === wftmAddress ||
                             tokenInAddress === axlUSDCAddress || tokenOutAddress === axlUSDCAddress;
  
  // Skip if tokenIn or tokenOut is already WFTM (unless checking axlUSDC)
  if (tokenInAddress === wftmAddress || tokenOutAddress === wftmAddress) {
    // Only proceed if we're checking WFTM-axlUSDC pair
    if (!(tokenInAddress === axlUSDCAddress || tokenOutAddress === axlUSDCAddress)) {
      return crossDexQuotes;
    }
  }
  
  console.log(`  🔀 Trying cross-DEX via WFTM...`);
  
  const v2Dexes = dexes.filter(d => d.type === 'v2');
  
  // Create all cross-DEX combination promises
  const crossDexPromises = [];
  
  for (const dex1 of v2Dexes) {
    for (const dex2 of v2Dexes) {
      // Skip if same DEX (that's handled by single-DEX routing)
      if (dex1.id === dex2.id) continue;
      
      crossDexPromises.push(
        (async () => {
          try {
            const factory1 = new ethers.Contract(dex1.factory, UNISWAP_V2_FACTORY_ABI, provider);
            const factory2 = new ethers.Contract(dex2.factory, UNISWAP_V2_FACTORY_ABI, provider);
            
            // Check both pools in parallel
            const [pair1, pair2] = await Promise.all([
              factory1.getPair(tokenInAddress, wftmAddress),
              factory2.getPair(wftmAddress, tokenOutAddress)
            ]);
            
            if (pair1 === ethers.ZeroAddress || pair2 === ethers.ZeroAddress) {
              return null;
            }
            
            console.log(`    ✓ Found cross-DEX path: ${dex1.name} (${tokenIn.symbol}→WFTM) → ${dex2.name} (WFTM→${tokenOut.symbol})`);
            
            const router1 = new ethers.Contract(dex1.router, UNISWAP_V2_ROUTER_ABI, provider);
            const router2 = new ethers.Contract(dex2.router, UNISWAP_V2_ROUTER_ABI, provider);
            
            // Get quotes for both hops in parallel
            const path1 = [tokenInAddress, wftmAddress];
            const path2 = [wftmAddress, tokenOutAddress];
            
            const amounts1 = await router1.getAmountsOut(amountIn, path1);
            const wftmAmount = amounts1[amounts1.length - 1];
            
            const amounts2 = await router2.getAmountsOut(wftmAmount, path2);
            const finalAmount = amounts2[amounts2.length - 1];
            
            if (finalAmount > 0) {
              console.log(`    ✓ Cross-DEX quote: ${ethers.formatUnits(finalAmount, tokenOut.decimals)} ${tokenOut.symbol}`);
              return {
                dex: { name: `${dex1.name}+${dex2.name}`, id: `${dex1.id}_${dex2.id}` },
                dex1: dex1,
                dex2: dex2,
                path: [tokenInAddress, wftmAddress, tokenOutAddress],
                intermediateToken: wftmAddress,
                intermediateAmount: wftmAmount, // WFTM amount from first swap
                amountOut: finalAmount,
                type: 'v2',
                crossDex: true,
                directPath: false,
              };
            }
            return null;
          } catch (error) {
            return null;
          }
        })()
      );
    }
  }
  
  // Execute all cross-DEX checks in parallel
  const results = await Promise.all(crossDexPromises);
  
  // Filter out null results
  return results.filter(r => r !== null);
};

// Get best route (wrapper for getAllQuotes)
export const getBestRoute = async (provider, tokenIn, tokenOut, amountIn) => {
  return getAllQuotes(provider, tokenIn, tokenOut, amountIn);
};

// Check if a V2 pair exists
export const checkV2PairExists = async (provider, dexConfig, tokenA, tokenB) => {
  try {
    const factory = new ethers.Contract(dexConfig.factory, UNISWAP_V2_FACTORY_ABI, provider);
    const tokenAAddress = normalizeTokenAddress(tokenA.address);
    const tokenBAddress = normalizeTokenAddress(tokenB.address);
    
    const pairAddress = await factory.getPair(tokenAAddress, tokenBAddress);
    return pairAddress !== ethers.ZeroAddress;
  } catch (error) {
    console.error(`Error checking V2 pair existence on ${dexConfig.name}:`, error);
    return false;
  }
};

// Check if a V3 pool exists
export const checkV3PoolExists = async (provider, dexConfig, tokenA, tokenB, fee) => {
  try {
    const factory = new ethers.Contract(dexConfig.factory, UNISWAP_V3_FACTORY_ABI, provider);
    const tokenAAddress = normalizeTokenAddress(tokenA.address);
    const tokenBAddress = normalizeTokenAddress(tokenB.address);
    
    const poolAddress = await factory.getPool(tokenAAddress, tokenBAddress, fee);
    return poolAddress !== ethers.ZeroAddress;
  } catch (error) {
    console.error(`Error checking V3 pool existence on ${dexConfig.name}:`, error);
    return false;
  }
};
